import {
  AgentComplianceReviewDocumentQuery,
  GetIngestionDocumentFragment,
} from '@client/graphql/__generated__/types';
import { useMemo } from 'react';
import {
  buildComplianceFieldHighlights,
  buildValidationFieldsMap,
  FieldsByContractDocument,
  getValidationItemFieldIds,
  ValidationField,
} from '~/common/pages/Compliance/utils/fieldHighlights.utils';
import { getValidationResultsForDocument } from '~/common/pages/Compliance/utils/validation.utils';
import {
  ComplianceSuccessfulCheck,
  ComplianceSuggestion,
} from '../components/types';

export type { ValidationField };

type AgentDocumentDetail = NonNullable<
  AgentComplianceReviewDocumentQuery['agentComplianceReviewDocument']
>;

type ValidationData = Parameters<typeof getValidationResultsForDocument>[1];

export interface AgentDocumentValidation {
  /** Failed checks, in the right-rail "suggestion" framing. */
  suggestions: ComplianceSuggestion[];
  /** Passed checks. */
  successfulChecks: ComplianceSuccessfulCheck[];
  /** Field ids to paint as failed (yellow warnings on the agent page). */
  errorFieldIds: string[];
  /** Field ids to paint as passed (green). */
  successFieldIds: string[];
  /** Per-field message for failed fields. */
  fieldMessages: Record<string, string>;
  /** Page → field boxes for the PDF overlay. */
  validationFieldsMap: Map<number, ValidationField[]>;
}

const EMPTY: AgentDocumentValidation = {
  suggestions: [],
  successfulChecks: [],
  errorFieldIds: [],
  successFieldIds: [],
  fieldMessages: {},
  validationFieldsMap: new Map(),
};

/**
 * Derives the agent compliance review per-document display — suggestions,
 * successful checks, and PDF field highlights — from the RAW document detail
 * returned by the backend (repositioned `fields`, `contractDocuments`,
 * `contractDocumentPages`, and the raw `validationResults`/`aiValidationResults`
 * arrays).
 *
 * It runs the SAME shared `getValidationResultsForDocument` the compliance
 * drawer + extension use (AI-prioritized split) and the shared
 * `buildComplianceFieldHighlights`/`getValidationItemFieldIds` field-matching,
 * so the agent page never forks that transformation onto the backend.
 */
export const useAgentDocumentValidation = (
  detail: AgentDocumentDetail | null | undefined
): AgentDocumentValidation =>
  useMemo(() => {
    if (!detail) {
      return EMPTY;
    }

    const fields = (detail.fields ?? {}) as FieldsByContractDocument;

    // The shared util reads these off a GetIngestionDocumentFragment-shaped
    // document + validation report data; we supply only the fields it touches
    // and cast (the same adapter pattern the extension hook uses).
    const documentLike = {
      documentId: detail.documentId,
      contractDocuments: detail.contractDocuments,
      // `fields` / `contractDocumentPages` are JSON scalars (typed `any` by
      // codegen); narrow to `unknown` before the adapter cast.
      fields: detail.fields as unknown,
      contractDocumentPages: detail.contractDocumentPages as unknown,
    } as unknown as GetIngestionDocumentFragment;

    const validationData = {
      documentValidations: [
        {
          id: detail.ingestionDocumentId,
          validationResults: detail.validationResults as unknown,
          aiValidationResults: detail.aiValidationResults as unknown,
        },
      ],
    } as unknown as ValidationData;

    const { errors, successes } = getValidationResultsForDocument(
      documentLike,
      validationData
    );

    const highlights = buildComplianceFieldHighlights(
      errors,
      successes,
      fields
    );

    const suggestions: ComplianceSuggestion[] = errors.map((error) => ({
      message: error.message,
      pageNumbers: error.pageNumbers,
      ruleUuid: error.ruleUuid,
      isAddedByUser: false,
      fieldIds: getValidationItemFieldIds(error, fields),
    }));

    const successfulChecks: ComplianceSuccessfulCheck[] = successes.map(
      (success) => ({
        message: success.message,
        pageNumbers: success.pageNumbers,
        ruleUuid: success.ruleUuid,
        fieldIds: getValidationItemFieldIds(success, fields),
      })
    );

    return {
      suggestions,
      successfulChecks,
      ...highlights,
      validationFieldsMap: buildValidationFieldsMap(fields),
    };
  }, [detail]);
